import { useState, useEffect, useCallback } from 'react';
import { api } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import { 
  Globe, Search, Sparkles, AlertTriangle, Shield, Plus, Trash2, CheckCircle, 
  Link2, Server, FileSpreadsheet, Filter, X, Eye, ExternalLink, ChevronDown, Activity,
  Lightbulb, ArrowRight, Download, Database, Smartphone, Cpu, Radar, Cloud,
  Play, Pause, RefreshCw, Settings, Wifi, Network, ChevronRight, Wrench, Lock,
  LayoutGrid, Table2, Calendar, Clock, CheckSquare, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function AssetManagement() {
  const { isDarkMode } = useTheme();
  const [activeMainTab, setActiveMainTab] = useState('inventory');
  
  // Common state
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Inventory state (Internal Assets)
  const [internalAssets, setInternalAssets] = useState([]);
  const [typeFilter, setTypeFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // grid or table
  const [addAssetDialogOpen, setAddAssetDialogOpen] = useState(false);
  const [newAsset, setNewAsset] = useState({ name: '', type: 'web', url: '', ip_address: '' });
  
  // EASM state (External Attack Surface)
  const [externalAssets, setExternalAssets] = useState([]);
  const [domains, setDomains] = useState([]);
  const [threats, setThreats] = useState([]);
  const [discovering, setDiscovering] = useState(false);
  const [exposureFilter, setExposureFilter] = useState('all');
  const [domainFilter, setDomainFilter] = useState('all');
  const [addDomainDialogOpen, setAddDomainDialogOpen] = useState(false);
  const [newDomain, setNewDomain] = useState({ domain: '', description: '' });
  const [assetDetailOpen, setAssetDetailOpen] = useState(false);
  const [domainDetailOpen, setDomainDetailOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [deleteDomainDialogOpen, setDeleteDomainDialogOpen] = useState(false);
  const [addingDomain, setAddingDomain] = useState(false);
  
  // Enterprise EASM State
  const [easmTarget, setEasmTarget] = useState('');
  const [runningEasmScan, setRunningEasmScan] = useState(false);
  const [easmDeepScan, setEasmDeepScan] = useState(false);
  const [easmHistory, setEasmHistory] = useState([]);
  
  // Scheduled EASM Scan state
  const [scheduledScans, setScheduledScans] = useState([]);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [newScheduledScan, setNewScheduledScan] = useState({
    name: '',
    frequency: 'daily',
    scan_all_domains: true,
    domain_ids: [],
    notify_on_new_assets: true,
    notify_on_drift: true
  });
  const [creatingSchedule, setCreatingSchedule] = useState(false);
  
  // Discovery state (Network Scanning)
  const [scans, setScans] = useState([]);
  const [discoveredAssets, setDiscoveredAssets] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanStatus, setScanStatus] = useState('idle');
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [scanConfig, setScanConfig] = useState({
    scan_type: 'network',
    target_range: '192.168.1.0/24',
    ports: '22,80,443,3306,5432,8080',
    scan_depth: 'standard',
    include_services: true,
    include_os_detection: true,
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchInternalAssets(),
        fetchExternalAssets(),
        fetchDomains(),
        fetchThreats(),
        fetchScans(),
        fetchDiscoveredAssets(),
        fetchScheduledScans(),
        fetchEasmHistory()
      ]);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Internal Assets functions
  const fetchInternalAssets = async () => {
    try {
      const response = await api.get('/assets');
      setInternalAssets(response.data || []);
    } catch (error) {
      console.error('Failed to fetch internal assets');
    }
  };

  const handleCreateAsset = async (e) => {
    e.preventDefault();
    try {
      await api.post('/assets', newAsset);
      toast.success('Asset created successfully');
      setAddAssetDialogOpen(false);
      setNewAsset({ name: '', type: 'web', url: '', ip_address: '' });
      fetchInternalAssets();
    } catch (error) {
      toast.error('Failed to create asset');
    }
  };

  const [deleteAssetDialogOpen, setDeleteAssetDialogOpen] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState(null);
  
  // Bulk selection state
  const [selectedAssets, setSelectedAssets] = useState(new Set());
  const [selectedExternalAssets, setSelectedExternalAssets] = useState(new Set());
  const [selectedDiscoveredAssets, setSelectedDiscoveredAssets] = useState(new Set());
  
  // Bulk promote dialog state
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
  const [promoteSource, setPromoteSource] = useState(''); // 'external' or 'discovered'
  const [promoteConfig, setPromoteConfig] = useState({
    group: '',
    tags: '',
    category: 'external',
    type: 'web' // Use 'web' as default type (valid enum value)
  });

  const handleDeleteAsset = async () => {
    if (!assetToDelete) return;
    try {
      await api.delete(`/assets/${assetToDelete.id}`);
      toast.success('Asset deleted successfully');
      setDeleteAssetDialogOpen(false);
      setAssetToDelete(null);
      fetchInternalAssets();
    } catch (error) {
      toast.error('Failed to delete asset');
    }
  };

  // Bulk selection functions
  const toggleAssetSelection = (assetId) => {
    setSelectedAssets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(assetId)) {
        newSet.delete(assetId);
      } else {
        newSet.add(assetId);
      }
      return newSet;
    });
  };

  const toggleSelectAllAssets = (assetList) => {
    const allIds = new Set(assetList.map(a => a.id));
    const allSelected = assetList.every(a => selectedAssets.has(a.id));
    if (allSelected) {
      setSelectedAssets(prev => {
        const newSet = new Set(prev);
        allIds.forEach(id => newSet.delete(id));
        return newSet;
      });
    } else {
      setSelectedAssets(prev => new Set([...prev, ...allIds]));
    }
  };

  const handleBulkDeleteAssets = async () => {
    if (selectedAssets.size === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedAssets.size} asset(s)?`)) return;
    
    let successCount = 0;
    let failCount = 0;
    
    for (const id of selectedAssets) {
      try {
        await api.delete(`/assets/${id}`);
        successCount++;
      } catch (error) {
        failCount++;
      }
    }
    
    if (successCount > 0) {
      toast.success(`Deleted ${successCount} asset(s)`);
    }
    if (failCount > 0) {
      toast.error(`Failed to delete ${failCount} asset(s)`);
    }
    
    setSelectedAssets(new Set());
    fetchInternalAssets();
  };

  const handleBulkScanAssets = async () => {
    if (selectedAssets.size === 0) return;
    
    let successCount = 0;
    let failCount = 0;
    
    for (const id of selectedAssets) {
      try {
        await api.post(`/assets/${id}/scan`);
        successCount++;
      } catch (error) {
        failCount++;
      }
    }
    
    if (successCount > 0) {
      toast.success(`Initiated scan for ${successCount} asset(s)`);
    }
    if (failCount > 0) {
      toast.error(`Failed to scan ${failCount} asset(s)`);
    }
    
    setSelectedAssets(new Set());
  };

  // External Assets Bulk Selection Functions
  const toggleExternalAssetSelection = (assetId) => {
    setSelectedExternalAssets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(assetId)) {
        newSet.delete(assetId);
      } else {
        newSet.add(assetId);
      }
      return newSet;
    });
  };

  const toggleSelectAllExternalAssets = (assetList) => {
    const allIds = new Set(assetList.map(a => a.id));
    const allSelected = assetList.every(a => selectedExternalAssets.has(a.id));
    if (allSelected) {
      setSelectedExternalAssets(new Set());
    } else {
      setSelectedExternalAssets(allIds);
    }
  };

  const handleBulkDeleteExternalAssets = async () => {
    if (selectedExternalAssets.size === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedExternalAssets.size} external asset(s)?`)) return;
    
    let successCount = 0;
    let failCount = 0;
    
    for (const id of selectedExternalAssets) {
      try {
        await api.delete(`/external-assets/${id}`);
        successCount++;
      } catch (error) {
        failCount++;
      }
    }
    
    if (successCount > 0) {
      toast.success(`Deleted ${successCount} external asset(s)`);
    }
    if (failCount > 0) {
      toast.error(`Failed to delete ${failCount} asset(s)`);
    }
    
    setSelectedExternalAssets(new Set());
    fetchExternalAssets();
  };

  const handleBulkPromoteExternalAssets = async () => {
    if (selectedExternalAssets.size === 0) return;
    setPromoteSource('external');
    setPromoteConfig({
      group: '',
      tags: 'easm-discovered',
      category: 'external',
      type: 'web' // Use 'web' as default type (valid enum value)
    });
    setPromoteDialogOpen(true);
  };

  const handleBulkPromoteDiscoveredAssets = async () => {
    if (selectedDiscoveredAssets.size === 0) return;
    setPromoteSource('discovered');
    setPromoteConfig({
      group: '',
      tags: 'network-discovered',
      category: 'internal',
      type: 'infrastructure'
    });
    setPromoteDialogOpen(true);
  };

  const executeBulkPromote = async () => {
    let successCount = 0;
    let failCount = 0;
    const tagsArray = promoteConfig.tags.split(',').map(t => t.trim()).filter(t => t);
    
    if (promoteSource === 'external') {
      for (const id of selectedExternalAssets) {
        const asset = externalAssets.find(a => a.id === id);
        if (asset) {
          try {
            await api.post('/assets', {
              name: asset.host || asset.name || asset.domain,
              type: promoteConfig.type,
              url: asset.host?.startsWith('http') ? asset.host : `https://${asset.host}`,
              ip_address: asset.ip_address || '',
              description: `Promoted from EASM discovery. Domain: ${asset.domain || 'N/A'}. Source: ${asset.source || 'EASM'}${promoteConfig.group ? `. Group: ${promoteConfig.group}` : ''}`,
              category: promoteConfig.category,
              exposure_level: asset.exposure_level || 'low',
              tags: tagsArray,
              group: promoteConfig.group || undefined
            });
            successCount++;
          } catch (error) {
            failCount++;
          }
        }
      }
      setSelectedExternalAssets(new Set());
    } else if (promoteSource === 'discovered') {
      for (const id of selectedDiscoveredAssets) {
        const asset = discoveredAssets.find(a => a.id === id);
        if (asset) {
          try {
            await api.post('/assets', {
              name: asset.hostname || asset.ip_address,
              type: promoteConfig.type,
              ip_address: asset.ip_address || '',
              description: `Promoted from network discovery. OS: ${asset.os || 'Unknown'}. Services: ${asset.services?.join(', ') || 'N/A'}${promoteConfig.group ? `. Group: ${promoteConfig.group}` : ''}`,
              category: promoteConfig.category,
              tags: tagsArray,
              group: promoteConfig.group || undefined
            });
            successCount++;
          } catch (error) {
            failCount++;
          }
        }
      }
      setSelectedDiscoveredAssets(new Set());
    }
    
    if (successCount > 0) {
      toast.success(`Promoted ${successCount} asset(s) to inventory`);
    }
    if (failCount > 0) {
      toast.error(`Failed to promote ${failCount} asset(s)`);
    }
    
    setPromoteDialogOpen(false);
    fetchInternalAssets();
  };

  const promoteExternalAsset = async (asset) => {
    try {
      await api.post('/assets', {
        name: asset.host || asset.name || asset.domain,
        type: 'web', // Use 'web' as default type for external assets (valid enum value)
        url: asset.host?.startsWith('http') ? asset.host : `https://${asset.host}`,
        ip_address: asset.ip_address || '',
        description: `Promoted from EASM discovery. Domain: ${asset.domain || 'N/A'}. Source: ${asset.source || 'EASM'}`,
        category: 'external',
        exposure_level: asset.exposure_level || 'low',
        tags: ['easm-discovered', 'external']
      });
      toast.success('Asset promoted to inventory');
      fetchInternalAssets();
    } catch (error) {
      toast.error('Failed to promote asset');
    }
  };

  // Discovered Assets (Network) Bulk Selection Functions
  const toggleDiscoveredAssetSelection = (assetId) => {
    setSelectedDiscoveredAssets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(assetId)) {
        newSet.delete(assetId);
      } else {
        newSet.add(assetId);
      }
      return newSet;
    });
  };

  const toggleSelectAllDiscoveredAssets = (assetList) => {
    const allIds = new Set(assetList.map(a => a.id));
    const allSelected = assetList.every(a => selectedDiscoveredAssets.has(a.id));
    if (allSelected) {
      setSelectedDiscoveredAssets(new Set());
    } else {
      setSelectedDiscoveredAssets(allIds);
    }
  };

  const handleBulkDeleteDiscoveredAssets = async () => {
    if (selectedDiscoveredAssets.size === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedDiscoveredAssets.size} discovered asset(s)?`)) return;
    
    let successCount = 0;
    let failCount = 0;
    
    for (const id of selectedDiscoveredAssets) {
      try {
        await api.delete(`/discovered-assets/${id}`);
        successCount++;
      } catch (error) {
        failCount++;
      }
    }
    
    if (successCount > 0) {
      toast.success(`Deleted ${successCount} discovered asset(s)`);
    }
    if (failCount > 0) {
      toast.error(`Failed to delete ${failCount} asset(s)`);
    }
    
    setSelectedDiscoveredAssets(new Set());
    fetchDiscoveredAssets();
  };

  // EASM functions
  const fetchExternalAssets = async () => {
    try {
      const response = await api.get('/easm/assets');
      setExternalAssets(response.data.assets || []);
    } catch (error) {
      console.error('Failed to fetch external assets');
    }
  };

  const fetchDomains = async () => {
    try {
      const response = await api.get('/easm/domains');
      setDomains(response.data.domains || []);
    } catch (error) {
      console.error('Failed to fetch domains');
    }
  };

  const fetchThreats = async () => {
    try {
      const response = await api.get('/easm/threats');
      setThreats(response.data.threats || []);
    } catch (error) {
      console.error('Failed to fetch threats');
    }
  };

  const fetchEasmHistory = async () => {
    try {
      const response = await api.get('/easm/enterprise/history?limit=10');
      setEasmHistory(response.data || []);
    } catch (error) {
      console.error('Failed to fetch EASM history');
    }
  };

  const handleEnterpriseEasmScan = async () => {
    if (!easmTarget) {
      toast.error('Please enter a target domain');
      return;
    }
    
    setRunningEasmScan(true);
    try {
      const response = await api.post('/easm/enterprise/scan', {
        target_domain: easmTarget,
        deep_scan: easmDeepScan
      });
      
      toast.success(`EASM scan completed: ${response.data.assets_count} assets discovered. Risk Score: ${response.data.risk_score?.toFixed(1)}`);
      fetchEasmHistory();
      fetchExternalAssets();
      setEasmTarget('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'EASM scan failed');
    } finally {
      setRunningEasmScan(false);
    }
  };

  // Scheduled EASM Scan functions
  const fetchScheduledScans = async () => {
    try {
      const response = await api.get('/easm/scheduled-scans');
      setScheduledScans(response.data.scans || []);
    } catch (error) {
      console.error('Failed to fetch scheduled scans');
    }
  };

  const handleCreateScheduledScan = async (e) => {
    e.preventDefault();
    if (!newScheduledScan.name.trim()) {
      toast.error('Please enter a scan name');
      return;
    }
    setCreatingSchedule(true);
    try {
      await api.post('/easm/scheduled-scans', newScheduledScan);
      toast.success('Scheduled scan created');
      setScheduleDialogOpen(false);
      setNewScheduledScan({
        name: '',
        frequency: 'daily',
        scan_all_domains: true,
        domain_ids: [],
        notify_on_new_assets: true,
        notify_on_drift: true
      });
      fetchScheduledScans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create scheduled scan');
    } finally {
      setCreatingSchedule(false);
    }
  };

  const handleToggleSchedule = async (scan) => {
    try {
      await api.put(`/easm/scheduled-scans/${scan.id}`, { is_active: !scan.is_active });
      toast.success(scan.is_active ? 'Scheduled scan paused' : 'Scheduled scan activated');
      fetchScheduledScans();
    } catch (error) {
      toast.error('Failed to update scheduled scan');
    }
  };

  const handleDeleteSchedule = async (scanId) => {
    try {
      await api.delete(`/easm/scheduled-scans/${scanId}`);
      toast.success('Scheduled scan deleted');
      fetchScheduledScans();
    } catch (error) {
      toast.error('Failed to delete scheduled scan');
    }
  };

  const handleRunScheduleNow = async (scanId) => {
    try {
      await api.post(`/easm/scheduled-scans/${scanId}/run-now`);
      toast.success('Scan triggered - running in background');
    } catch (error) {
      toast.error('Failed to trigger scan');
    }
  };

  const handleAddDomain = async (e) => {
    e.preventDefault();
    if (!newDomain.domain.trim()) {
      toast.error('Please enter a domain');
      return;
    }
    setAddingDomain(true);
    try {
      const response = await api.post('/easm/domains', newDomain);
      toast.success('Domain added successfully');
      setAddDomainDialogOpen(false);
      setNewDomain({ domain: '', description: '' });
      
      // Immediately update local state with the new domain
      if (response.data?.domain) {
        setDomains(prev => [...prev, response.data.domain]);
      }
      
      // Also fetch to ensure sync
      setTimeout(() => fetchDomains(), 500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add domain');
    } finally {
      setAddingDomain(false);
    }
  };

  const handleDeleteDomain = async () => {
    if (!selectedDomain) return;
    try {
      await api.delete(`/easm/domains/${selectedDomain.id}`);
      toast.success('Domain deleted successfully');
      setDeleteDomainDialogOpen(false);
      setSelectedDomain(null);
      fetchDomains();
    } catch (error) {
      toast.error('Failed to delete domain');
    }
  };

  const startDiscovery = async () => {
    if (!domains || domains.length === 0) {
      toast.error('Please add at least one domain first');
      return;
    }
    
    setDiscovering(true);
    setDiscoveryProgress(0);
    
    try {
      // Start the discovery
      const response = await api.post('/easm/discover');
      toast.success('EASM Discovery started - scanning Certificate Transparency logs');
      
      // Poll for results over 15 seconds (crt.sh can take time)
      let attempts = 0;
      const maxAttempts = 6;
      
      const pollResults = setInterval(async () => {
        attempts++;
        setDiscoveryProgress(Math.min((attempts / maxAttempts) * 100, 95));
        
        try {
          await fetchExternalAssets();
          await fetchThreats();
          
          // Check if we got new results
          if (attempts >= maxAttempts) {
            clearInterval(pollResults);
            setDiscovering(false);
            setDiscoveryProgress(100);
            toast.success('Discovery complete - found assets from Certificate Transparency logs');
          }
        } catch (error) {
          console.error('Polling error:', error);
        }
      }, 2500);
      
    } catch (error) {
      console.error('Discovery error:', error);
      toast.error('Failed to start discovery: ' + (error.response?.data?.detail || error.message));
      setDiscovering(false);
    }
  };
  
  const [discoveryProgress, setDiscoveryProgress] = useState(0);

  // Discovery functions
  const fetchScans = async () => {
    try {
      const response = await api.get('/asset-discovery/scans');
      setScans(response.data || []);
    } catch (error) {
      console.error('Failed to fetch scans');
    }
  };

  const fetchDiscoveredAssets = async () => {
    try {
      const response = await api.get('/asset-discovery/discovered');
      setDiscoveredAssets(response.data || []);
    } catch (error) {
      console.error('Failed to fetch discovered assets');
    }
  };

  // Delete external asset (from EASM discovery)
  const deleteExternalAsset = async (assetId) => {
    try {
      await api.delete(`/external-assets/${assetId}`);
      toast.success('External asset deleted');
      fetchExternalAssets();
    } catch (error) {
      toast.error('Failed to delete asset');
    }
  };

  // Delete discovered asset (from network discovery)
  const deleteDiscoveredAsset = async (assetId) => {
    try {
      await api.delete(`/discovered-assets/${assetId}`);
      toast.success('Discovered asset deleted');
      fetchDiscoveredAssets();
    } catch (error) {
      toast.error('Failed to delete asset');
    }
  };

  const startScan = async () => {
    setScanning(true);
    setScanStatus('running');
    setScanProgress(0);
    try {
      await api.post('/asset-discovery/scan', scanConfig);
      // Simulate progress
      const interval = setInterval(() => {
        setScanProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            setScanning(false);
            setScanStatus('completed');
            fetchDiscoveredAssets();
            fetchScans();
            toast.success('Scan completed');
            return 100;
          }
          return prev + 10;
        });
      }, 500);
    } catch (error) {
      toast.error('Failed to start scan');
      setScanning(false);
      setScanStatus('failed');
    }
  };

  const promoteAsset = async (asset) => {
    try {
      await api.post('/asset-discovery/promote', { asset_id: asset.id });
      toast.success('Asset promoted to inventory');
      fetchDiscoveredAssets();
      fetchInternalAssets();
    } catch (error) {
      toast.error('Failed to promote asset');
    }
  };

  // Filtered data
  const filteredInternalAssets = internalAssets.filter(asset => {
    const matchesSearch = asset.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         asset.url?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || asset.type === typeFilter;
    return matchesSearch && matchesType;
  });

  const filteredExternalAssets = externalAssets.filter(asset => {
    const matchesSearch = asset.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         asset.host?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesExposure = exposureFilter === 'all' || asset.exposure_level === exposureFilter;
    const matchesDomain = domainFilter === 'all' || asset.domain_id === domainFilter;
    return matchesSearch && matchesExposure && matchesDomain;
  });

  const filteredDiscoveredAssets = discoveredAssets.filter(asset => {
    return asset.hostname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           asset.ip_address?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Statistics
  const totalAssets = internalAssets.length + externalAssets.length + discoveredAssets.length;
  const criticalCount = externalAssets.filter(a => a.exposure_level === 'critical').length;
  const highCount = externalAssets.filter(a => a.exposure_level === 'high').length;

  const getTypeIcon = (type) => {
    switch (type) {
      case 'web': return <Globe className="w-4 h-4" />;
      case 'api': return <Server className="w-4 h-4" />;
      case 'mobile': return <Smartphone className="w-4 h-4" />;
      case 'infrastructure': return <Cpu className="w-4 h-4" />;
      case 'database': return <Database className="w-4 h-4" />;
      default: return <Server className="w-4 h-4" />;
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      web: 'bg-blue-500/20 text-blue-400',
      api: 'bg-emerald-500/20 text-emerald-400',
      mobile: 'bg-purple-500/20 text-purple-400',
      infrastructure: 'bg-orange-500/20 text-orange-400',
      database: 'bg-red-500/20 text-red-400'
    };
    return colors[type] || 'bg-zinc-500/20 text-zinc-400';
  };

  const getExposureBadge = (level) => {
    const colors = {
      critical: 'bg-red-500/20 text-red-500',
      high: 'bg-orange-500/20 text-orange-500',
      medium: 'bg-yellow-500/20 text-yellow-500',
      low: 'bg-green-500/20 text-green-500'
    };
    return colors[level] || 'bg-zinc-500/20 text-zinc-500';
  };

  const getRemediationSteps = (asset) => {
    const steps = [];
    
    if (asset.open_ports?.includes(22)) {
      steps.push({
        severity: 'high',
        issue: 'SSH Port (22) Exposed',
        action: 'Restrict SSH access to specific IP ranges using firewall rules. Consider using SSH keys instead of passwords.',
        command: 'ufw allow from <trusted_ip> to any port 22'
      });
    }
    
    if (asset.open_ports?.includes(3306)) {
      steps.push({
        severity: 'critical',
        issue: 'MySQL Port (3306) Exposed',
        action: 'Never expose database ports publicly. Use a VPN or SSH tunnel for remote access.',
        command: 'mysql -h localhost -P 3306 --protocol=TCP'
      });
    }
    
    if (asset.open_ports?.includes(80) && !asset.open_ports?.includes(443)) {
      steps.push({
        severity: 'medium',
        issue: 'HTTP without HTTPS',
        action: 'Enable HTTPS with a valid SSL certificate. Redirect all HTTP traffic to HTTPS.',
        command: 'certbot --nginx -d yourdomain.com'
      });
    }
    
    if (asset.technologies?.includes('WordPress')) {
      steps.push({
        severity: 'medium',
        issue: 'WordPress Detected',
        action: 'Ensure WordPress core, themes, and plugins are updated. Consider using a WAF.',
        command: 'wp core update && wp plugin update --all'
      });
    }
    
    if (asset.ssl_issues) {
      steps.push({
        severity: 'high',
        issue: 'SSL Certificate Issues',
        action: 'Renew or fix SSL certificate. Ensure TLS 1.2+ is enabled and weak ciphers are disabled.',
        command: 'certbot renew --force-renewal'
      });
    }
    
    if (steps.length === 0) {
      steps.push({
        severity: 'info',
        issue: 'No Critical Issues Found',
        action: 'Continue regular security monitoring and vulnerability scanning.',
        command: null
      });
    }
    
    return steps;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            isDarkMode 
              ? 'bg-sky-500/15 border border-sky-500/30' 
              : 'bg-sky-100 border border-sky-200'
          }`}>
            <Globe className={`w-5 h-5 ${isDarkMode ? 'text-sky-400' : 'text-sky-600'}`} />
          </div>
          <div>
            <h1 className={`font-inter font-bold text-xl lg:text-2xl tracking-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Asset Management
            </h1>
            <p className={`text-sm font-inter mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              Unified view of internal assets, external attack surface, and discovered resources
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={fetchAllData}
            variant="outline"
            size="sm"
            className={isDarkMode ? 'border-navy-700 text-slate-300 hover:bg-navy-800 hover:border-electric-500/50' : ''}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 lg:gap-4">
        <div className={`p-4 rounded-xl border transition-all ${isDarkMode ? 'bg-navy-800/50 border-navy-700 hover:border-electric-500/50' : 'bg-white border-slate-200 shadow-card hover:shadow-card-hover'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-indigo-500/15 border border-indigo-500/30' : 'bg-indigo-100 border border-indigo-200'}`}>
              <Database className={`w-5 h-5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
            </div>
            <div>
              <p className={`text-2xl font-inter font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{totalAssets}</p>
              <p className={`text-xs font-inter ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>Total Assets</p>
            </div>
          </div>
        </div>
        <div className={`p-4 rounded-xl border transition-all ${isDarkMode ? 'bg-navy-800/50 border-navy-700 hover:border-electric-500/50' : 'bg-white border-slate-200 shadow-card hover:shadow-card-hover'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-emerald-500/15 border border-emerald-500/30' : 'bg-emerald-100 border border-emerald-200'}`}>
              <Server className={`w-5 h-5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
            </div>
            <div>
              <p className={`text-2xl font-inter font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{internalAssets.length}</p>
              <p className={`text-xs font-inter ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>Internal Assets</p>
            </div>
          </div>
        </div>
        <div className={`p-4 rounded-xl border transition-all ${isDarkMode ? 'bg-navy-800/50 border-navy-700 hover:border-electric-500/50' : 'bg-white border-slate-200 shadow-card hover:shadow-card-hover'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-red-500/15 border border-red-500/30' : 'bg-red-100 border border-red-200'}`}>
              <AlertTriangle className={`w-5 h-5 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
            </div>
            <div>
              <p className={`text-2xl font-inter font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{criticalCount}</p>
              <p className={`text-xs font-inter ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>Critical Exposure</p>
            </div>
          </div>
        </div>
        <div className={`p-4 rounded-xl border transition-all ${isDarkMode ? 'bg-navy-800/50 border-navy-700 hover:border-electric-500/50' : 'bg-white border-slate-200 shadow-card hover:shadow-card-hover'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-sky-500/15 border border-sky-500/30' : 'bg-sky-100 border border-sky-200'}`}>
              <Radar className={`w-5 h-5 ${isDarkMode ? 'text-sky-400' : 'text-sky-600'}`} />
            </div>
            <div>
              <p className={`text-2xl font-inter font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{discoveredAssets.length}</p>
              <p className={`text-xs font-inter ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>Discovered</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeMainTab} onValueChange={setActiveMainTab} className="space-y-4">
        <TabsList className={`grid w-full grid-cols-3 ${isDarkMode ? 'bg-zinc-900/60' : 'bg-slate-100'}`}>
          <TabsTrigger 
            value="inventory" 
            data-testid="tab-inventory"
            className={`${isDarkMode ? 'data-[state=active]:bg-indigo-600' : 'data-[state=active]:bg-indigo-600 data-[state=active]:text-white'}`}
          >
            <Server className="w-4 h-4 mr-2" />
            Asset Inventory
          </TabsTrigger>
          <TabsTrigger 
            value="attack-surface"
            data-testid="tab-attack-surface"
            className={`${isDarkMode ? 'data-[state=active]:bg-indigo-600' : 'data-[state=active]:bg-indigo-600 data-[state=active]:text-white'}`}
          >
            <Globe className="w-4 h-4 mr-2" />
            External Attack Surface
          </TabsTrigger>
          <TabsTrigger 
            value="discovery"
            data-testid="tab-discovery"
            className={`${isDarkMode ? 'data-[state=active]:bg-indigo-600' : 'data-[state=active]:bg-indigo-600 data-[state=active]:text-white'}`}
          >
            <Radar className="w-4 h-4 mr-2" />
            Discovery Scanner
          </TabsTrigger>
        </TabsList>

        {/* Search Bar - Common across tabs */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`} />
            <Input
              placeholder="Search assets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`pl-10 ${isDarkMode ? 'bg-zinc-900/50 border-white/10 text-white' : 'bg-white border-slate-300'}`}
            />
          </div>
        </div>

        {/* Asset Inventory Tab */}
        <TabsContent value="inventory" className="space-y-4">
          {/* Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{internalAssets.length}</p>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Total Assets</p>
            </div>
            <div className={`p-4 rounded-xl border border-l-2 border-l-blue-500 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <p className={`text-2xl font-bold text-blue-500`}>{internalAssets.filter(a => a.type === 'web').length}</p>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Web Apps</p>
            </div>
            <div className={`p-4 rounded-xl border border-l-2 border-l-emerald-500 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <p className={`text-2xl font-bold text-emerald-500`}>{internalAssets.filter(a => a.type === 'api').length}</p>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>APIs</p>
            </div>
            <div className={`p-4 rounded-xl border border-l-2 border-l-orange-500 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <p className={`text-2xl font-bold text-orange-500`}>{internalAssets.filter(a => a.type === 'infrastructure').length}</p>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Infrastructure</p>
            </div>
            <div className={`p-4 rounded-xl border border-l-2 border-l-red-500 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <p className={`text-2xl font-bold text-red-500`}>{internalAssets.filter(a => a.type === 'database').length}</p>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Databases</p>
            </div>
          </div>

          {/* Controls Row */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* View Toggle */}
                <div className={`flex rounded-lg border p-1 ${isDarkMode ? 'bg-zinc-900/50 border-white/10' : 'bg-slate-100 border-slate-200'}`}>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setViewMode('grid')}
                    className={`px-3 ${viewMode === 'grid' ? (isDarkMode ? 'bg-indigo-600 text-white' : 'bg-indigo-600 text-white') : ''}`}
                  >
                    <LayoutGrid className="w-4 h-4 mr-1" />
                    Grid
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setViewMode('table')}
                    className={`px-3 ${viewMode === 'table' ? (isDarkMode ? 'bg-indigo-600 text-white' : 'bg-indigo-600 text-white') : ''}`}
                  >
                    <Table2 className="w-4 h-4 mr-1" />
                    Table
                  </Button>
                </div>
                
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className={`w-40 ${isDarkMode ? 'bg-zinc-900/50 border-white/10 text-white' : ''}`}>
                    <SelectValue placeholder="Filter by type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="web">Web</SelectItem>
                    <SelectItem value="api">API</SelectItem>
                    <SelectItem value="mobile">Mobile</SelectItem>
                    <SelectItem value="infrastructure">Infrastructure</SelectItem>
                    <SelectItem value="database">Database</SelectItem>
                  </SelectContent>
                </Select>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleSelectAllAssets(filteredInternalAssets)}
                  className={isDarkMode ? 'border-white/10' : ''}
                  data-testid="select-all-assets"
                >
                  <CheckSquare className="w-4 h-4 mr-1" />
                  {filteredInternalAssets.every(a => selectedAssets.has(a.id)) && filteredInternalAssets.length > 0 ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              <Dialog open={addAssetDialogOpen} onOpenChange={setAddAssetDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-indigo-600 hover:bg-indigo-500 text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Asset
                  </Button>
                </DialogTrigger>
              <DialogContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
                <DialogHeader>
                  <DialogTitle>Add New Asset</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateAsset} className="space-y-4">
                  <div>
                    <Label>Name</Label>
                    <Input
                      value={newAsset.name}
                      onChange={(e) => setNewAsset({...newAsset, name: e.target.value})}
                      placeholder="Asset name"
                      className={isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}
                      required
                    />
                  </div>
                  <div>
                    <Label>Type</Label>
                    <Select value={newAsset.type} onValueChange={(v) => setNewAsset({...newAsset, type: v})}>
                      <SelectTrigger className={isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="web">Web Application</SelectItem>
                        <SelectItem value="api">API</SelectItem>
                        <SelectItem value="mobile">Mobile App</SelectItem>
                        <SelectItem value="infrastructure">Infrastructure</SelectItem>
                        <SelectItem value="database">Database</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>URL</Label>
                    <Input
                      value={newAsset.url}
                      onChange={(e) => setNewAsset({...newAsset, url: e.target.value})}
                      placeholder="https://..."
                      className={isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}
                    />
                  </div>
                  <div>
                    <Label>IP Address</Label>
                    <Input
                      value={newAsset.ip_address}
                      onChange={(e) => setNewAsset({...newAsset, ip_address: e.target.value})}
                      placeholder="192.168.1.1"
                      className={isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}
                    />
                  </div>
                  <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-500 text-white">
                    Create Asset
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            </div>
            
            {/* Bulk Actions Bar */}
            {selectedAssets.size > 0 && (
              <div className={`p-3 rounded-lg border ${isDarkMode ? 'bg-zinc-900/50 border-white/10' : 'bg-slate-50 border-slate-200'} flex items-center gap-3`}>
                <span className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                  <strong>{selectedAssets.size}</strong> asset(s) selected
                </span>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleBulkScanAssets} className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs" data-testid="bulk-scan-assets">
                    <Radar className="w-3 h-3 mr-1" />
                    Scan Selected
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleBulkDeleteAssets} className={`text-xs ${isDarkMode ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' : 'border-red-300 text-red-600 hover:bg-red-50'}`} data-testid="bulk-delete-assets">
                    <Trash2 className="w-3 h-3 mr-1" />
                    Delete Selected
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Grid View */}
          {viewMode === 'grid' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredInternalAssets.map((asset) => (
                <div
                  key={asset.id}
                  data-testid={`internal-asset-${asset.id}`}
                  className={`p-4 rounded-xl border transition-all ${
                    selectedAssets.has(asset.id) 
                      ? (isDarkMode ? 'bg-indigo-500/10 border-indigo-500/30' : 'bg-indigo-50 border-indigo-300')
                      : (isDarkMode ? 'bg-zinc-900/40 border-white/10 hover:border-indigo-500/30' : 'bg-white border-slate-200 hover:border-indigo-300')
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={selectedAssets.has(asset.id)}
                        onCheckedChange={() => toggleAssetSelection(asset.id)}
                        data-testid={`select-asset-${asset.id}`}
                      />
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-indigo-600/20' : 'bg-indigo-100'}`}>
                        {getTypeIcon(asset.type)}
                      </div>
                      <div>
                        <h3 className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          {asset.name}
                        </h3>
                        <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                          {asset.type}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded ${asset.status === 'active' ? 'bg-green-500/20 text-green-500' : 'bg-zinc-500/20 text-zinc-500'}`}>
                        {asset.status || 'active'}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`delete-asset-${asset.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setAssetToDelete(asset);
                          setDeleteAssetDialogOpen(true);
                        }}
                        className="h-7 w-7 p-0 hover:bg-red-500/10 text-red-500"
                        title="Delete asset"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  {asset.url && (
                    <p className={`text-xs font-mono truncate ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                      {asset.url}
                    </p>
                  )}
                  {asset.ip_address && (
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                      IP: {asset.ip_address}
                    </p>
                  )}
                  {asset.discovered_at && (
                    <p className={`text-xs mt-2 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`}>
                      Discovered: {new Date(asset.discovered_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              ))}
              {filteredInternalAssets.length === 0 && (
                <div className={`col-span-3 text-center py-12 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
                  <Server className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No internal assets found</p>
                  <p className="text-sm mt-1">Add your first asset to get started</p>
                </div>
              )}
            </div>
          )}

          {/* Table View */}
          {viewMode === 'table' && (
            <div className={`rounded-xl border overflow-hidden ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[800px]">
                  <thead className={isDarkMode ? 'bg-zinc-900/70' : 'bg-slate-50'}>
                    <tr>
                      <th className={`px-4 py-3 w-10`}></th>
                      <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Name</th>
                      <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Type</th>
                      <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>URL / IP</th>
                      <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Status</th>
                      <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Discovered</th>
                      <th className={`px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDarkMode ? 'divide-white/5' : 'divide-slate-100'}`}>
                    {filteredInternalAssets.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-4 py-12 text-center">
                          <Server className={`w-12 h-12 mx-auto mb-3 ${isDarkMode ? 'text-zinc-600' : 'text-slate-300'}`} />
                          <p className={isDarkMode ? 'text-zinc-400' : 'text-slate-500'}>No assets found</p>
                        </td>
                      </tr>
                    ) : (
                      filteredInternalAssets.map((asset) => (
                        <tr 
                          key={asset.id} 
                          className={`transition-colors ${
                            selectedAssets.has(asset.id)
                              ? (isDarkMode ? 'bg-indigo-500/10' : 'bg-indigo-50')
                              : (isDarkMode ? 'hover:bg-white/5' : 'hover:bg-slate-50')
                          }`}
                        >
                          <td className="px-4 py-4">
                            <Checkbox
                              checked={selectedAssets.has(asset.id)}
                              onCheckedChange={() => toggleAssetSelection(asset.id)}
                              data-testid={`select-asset-table-${asset.id}`}
                            />
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded flex items-center justify-center ${isDarkMode ? 'bg-indigo-600/20' : 'bg-indigo-100'}`}>
                                {getTypeIcon(asset.type)}
                              </div>
                              <span className={`text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{asset.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <span className={`px-2 py-1 rounded text-xs uppercase ${getTypeColor(asset.type)}`}>
                              {asset.type}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <div className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                              {asset.url ? (
                                <a href={asset.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-indigo-500">
                                  <span className="truncate max-w-[200px]">{asset.url}</span>
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              ) : asset.ip_address ? (
                                <span className="font-mono">{asset.ip_address}</span>
                              ) : '-'}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <span className={`px-2 py-1 rounded text-xs ${asset.status === 'active' ? 'bg-green-500/20 text-green-500' : 'bg-zinc-500/20 text-zinc-500'}`}>
                              {asset.status || 'active'}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                              {asset.discovered_at ? new Date(asset.discovered_at).toLocaleDateString() : '-'}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => { setAssetToDelete(asset); setDeleteAssetDialogOpen(true); }}
                              className="text-red-500 hover:bg-red-500/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {filteredInternalAssets.length > 0 && (
                <div className={`px-4 py-3 border-t ${isDarkMode ? 'bg-zinc-900/50 border-white/5' : 'bg-slate-50 border-slate-100'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                    Showing {filteredInternalAssets.length} of {internalAssets.length} assets
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Delete Asset Confirmation Dialog */}
          <AlertDialog open={deleteAssetDialogOpen} onOpenChange={setDeleteAssetDialogOpen}>
            <AlertDialogContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : ''}>
              <AlertDialogHeader>
                <AlertDialogTitle className={isDarkMode ? 'text-white' : ''}>Delete Asset</AlertDialogTitle>
                <AlertDialogDescription className={isDarkMode ? 'text-zinc-400' : ''}>
                  Are you sure you want to delete "{assetToDelete?.name}"? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className={isDarkMode ? 'border-white/10 text-white hover:bg-white/10' : ''}>
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteAsset}
                  className="bg-red-600 hover:bg-red-500 text-white"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </TabsContent>

        {/* External Attack Surface Tab */}
        <TabsContent value="attack-surface" className="space-y-4">
          {/* Enterprise EASM Scanner Panel */}
          <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
            <div className="flex flex-col lg:flex-row lg:items-end gap-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-purple-500/20 border border-purple-500/30' : 'bg-purple-100 border border-purple-200'}`}>
                  <Radar className={`w-5 h-5 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                </div>
                <div>
                  <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Enterprise Attack Surface Scanner</h3>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Comprehensive external asset discovery, DNS enumeration, port scanning & risk analysis</p>
                </div>
              </div>

              <div className="flex-1 flex flex-col sm:flex-row items-start sm:items-end gap-4">
                <div className="flex-1 w-full">
                  <Label className={`text-xs ${isDarkMode ? 'text-zinc-300' : 'text-slate-600'}`}>Target Domain</Label>
                  <Input
                    value={easmTarget}
                    onChange={(e) => setEasmTarget(e.target.value)}
                    placeholder="Enter target domain (e.g., example.com)"
                    className={`mt-1 text-sm h-12 text-base ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white placeholder:text-zinc-500' : 'bg-white border-slate-300 text-slate-900 placeholder:text-slate-400'}`}
                    data-testid="enterprise-easm-target-asset"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={easmDeepScan}
                      onCheckedChange={setEasmDeepScan}
                      data-testid="enterprise-easm-deep-scan-asset"
                    />
                    <Label className={`text-xs ${isDarkMode ? 'text-zinc-300' : 'text-slate-600'}`}>Deep Scan</Label>
                  </div>

                  <Button 
                    onClick={handleEnterpriseEasmScan}
                    disabled={runningEasmScan || !easmTarget}
                    className="bg-purple-600 hover:bg-purple-500"
                    data-testid="run-enterprise-easm-asset-btn"
                  >
                    {runningEasmScan ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Scanning...
                      </>
                    ) : (
                      <>
                        <Radar className="w-4 h-4 mr-2" />
                        Scan Attack Surface
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Recent Enterprise EASM Scans */}
            {easmHistory.length > 0 && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <h4 className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Recent Enterprise Scans</h4>
                <div className="flex flex-wrap gap-2">
                  {easmHistory.slice(0, 5).map(scan => (
                    <div key={scan.id} className={`px-3 py-2 rounded-lg text-xs flex items-center gap-2 ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-50'}`}>
                      <span className={isDarkMode ? 'text-white' : 'text-slate-900'}>{scan.target_domain}</span>
                      <span className={`px-1.5 py-0.5 rounded ${
                        scan.risk_score >= 70 ? 'bg-red-500/20 text-red-400' :
                        scan.risk_score >= 40 ? 'bg-orange-500/20 text-orange-400' :
                        'bg-green-500/20 text-green-400'
                      }`}>
                        {scan.assets_count || 0} assets
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Discovery Progress Banner */}
          {discovering && (
            <div className={`rounded-xl p-4 ${isDarkMode ? 'bg-indigo-500/10 border border-indigo-500/30' : 'bg-indigo-50 border border-indigo-200'}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <Sparkles className="w-5 h-5 text-indigo-500 animate-pulse" />
                  <span className={`font-medium ${isDarkMode ? 'text-indigo-400' : 'text-indigo-700'}`}>
                    EASM Discovery Running
                  </span>
                </div>
                <span className={`text-sm ${isDarkMode ? 'text-indigo-300' : 'text-indigo-600'}`}>
                  {Math.round(discoveryProgress)}%
                </span>
              </div>
              <div className={`h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-zinc-800' : 'bg-indigo-200'}`}>
                <div 
                  className="h-full bg-indigo-500 transition-all duration-500"
                  style={{ width: `${discoveryProgress}%` }}
                />
              </div>
              <p className={`text-xs mt-2 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                Scanning Certificate Transparency logs for subdomains and external assets...
              </p>
            </div>
          )}
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Select value={exposureFilter} onValueChange={setExposureFilter}>
                <SelectTrigger className={`w-40 ${isDarkMode ? 'bg-zinc-900/50 border-white/10 text-white' : ''}`}>
                  <SelectValue placeholder="Exposure level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
              <Select value={domainFilter} onValueChange={setDomainFilter}>
                <SelectTrigger className={`w-40 ${isDarkMode ? 'bg-zinc-900/50 border-white/10 text-white' : ''}`}>
                  <SelectValue placeholder="Domain" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Domains</SelectItem>
                  {domains.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.domain}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={startDiscovery}
                disabled={discovering || domains.length === 0}
                variant="outline"
                className={isDarkMode ? 'border-white/10 text-white hover:bg-white/10' : ''}
              >
                <Sparkles className={`w-4 h-4 mr-2 ${discovering ? 'animate-spin' : ''}`} />
                {discovering ? `Discovering... ${Math.round(discoveryProgress)}%` : 'Start Discovery'}
              </Button>
              <Dialog open={addDomainDialogOpen} onOpenChange={setAddDomainDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    data-testid="add-domain-btn"
                    className="bg-indigo-600 hover:bg-indigo-500 text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Domain
                  </Button>
                </DialogTrigger>
                <DialogContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white'}>
                  <DialogHeader>
                    <DialogTitle className={isDarkMode ? 'text-white' : 'text-slate-900'}>Add Domain for Monitoring</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddDomain} className="space-y-4">
                    <div>
                      <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Domain</Label>
                      <Input
                        data-testid="domain-input"
                        value={newDomain.domain}
                        onChange={(e) => setNewDomain({...newDomain, domain: e.target.value})}
                        placeholder="example.com"
                        className={isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}
                        required
                      />
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        Enter the root domain (e.g., company.com)
                      </p>
                    </div>
                    <div>
                      <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Description (Optional)</Label>
                      <Input
                        data-testid="domain-description"
                        value={newDomain.description}
                        onChange={(e) => setNewDomain({...newDomain, description: e.target.value})}
                        placeholder="Primary company domain"
                        className={isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}
                      />
                    </div>
                    <Button 
                      type="submit" 
                      data-testid="submit-domain-btn"
                      disabled={addingDomain} 
                      className="w-full bg-indigo-600 hover:bg-indigo-500 text-white"
                    >
                      {addingDomain ? 'Adding...' : 'Add Domain'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Domains List */}
          <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <h3 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Monitored Domains ({domains.length})
            </h3>
            <div className="flex flex-wrap gap-2">
              {domains.map((domain) => (
                <div
                  key={domain.id}
                  onClick={() => { setSelectedDomain(domain); setDomainDetailOpen(true); }}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all ${isDarkMode ? 'bg-zinc-800 hover:bg-zinc-700' : 'bg-slate-100 hover:bg-slate-200'}`}
                >
                  <Globe className="w-4 h-4 text-indigo-500" />
                  <span className={`text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{domain.domain}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${domain.verified ? 'bg-green-500/20 text-green-500' : 'bg-yellow-500/20 text-yellow-500'}`}>
                    {domain.verified ? 'Verified' : 'Pending'}
                  </span>
                </div>
              ))}
              {domains.length === 0 && (
                <p className={`text-sm ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
                  No domains added yet. Add your first domain to start monitoring.
                </p>
              )}
            </div>
          </div>

          {/* Scheduled EASM Scans */}
          <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-indigo-500" />
                <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Scheduled Scans ({scheduledScans.length})
                </h3>
              </div>
              <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    data-testid="add-schedule-btn"
                    size="sm"
                    className="bg-indigo-600 hover:bg-indigo-500 text-white"
                    disabled={domains.length === 0}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Schedule
                  </Button>
                </DialogTrigger>
                <DialogContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white'}>
                  <DialogHeader>
                    <DialogTitle className={isDarkMode ? 'text-white' : 'text-slate-900'}>
                      Create Scheduled EASM Scan
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateScheduledScan} className="space-y-4">
                    <div>
                      <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Scan Name</Label>
                      <Input
                        data-testid="schedule-name-input"
                        value={newScheduledScan.name}
                        onChange={(e) => setNewScheduledScan({...newScheduledScan, name: e.target.value})}
                        placeholder="Daily Security Scan"
                        className={isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}
                        required
                      />
                    </div>
                    <div>
                      <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Frequency</Label>
                      <Select 
                        value={newScheduledScan.frequency} 
                        onValueChange={(v) => setNewScheduledScan({...newScheduledScan, frequency: v})}
                      >
                        <SelectTrigger className={isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : ''}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Daily (3:00 AM)</SelectItem>
                          <SelectItem value="weekly">Weekly (Sunday 3:00 AM)</SelectItem>
                          <SelectItem value="biweekly">Bi-weekly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="scan-all-domains"
                        checked={newScheduledScan.scan_all_domains}
                        onChange={(e) => setNewScheduledScan({...newScheduledScan, scan_all_domains: e.target.checked})}
                        className="rounded"
                      />
                      <Label htmlFor="scan-all-domains" className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>
                        Scan all monitored domains
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="notify-new-assets"
                        checked={newScheduledScan.notify_on_new_assets}
                        onChange={(e) => setNewScheduledScan({...newScheduledScan, notify_on_new_assets: e.target.checked})}
                        className="rounded"
                      />
                      <Label htmlFor="notify-new-assets" className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>
                        Notify on new assets discovered
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="notify-drift"
                        checked={newScheduledScan.notify_on_drift}
                        onChange={(e) => setNewScheduledScan({...newScheduledScan, notify_on_drift: e.target.checked})}
                        className="rounded"
                      />
                      <Label htmlFor="notify-drift" className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>
                        Notify on configuration drift
                      </Label>
                    </div>
                    <Button 
                      type="submit" 
                      data-testid="submit-schedule-btn"
                      disabled={creatingSchedule} 
                      className="w-full bg-indigo-600 hover:bg-indigo-500 text-white"
                    >
                      {creatingSchedule ? 'Creating...' : 'Create Schedule'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            
            {scheduledScans.length > 0 ? (
              <div className="space-y-2">
                {scheduledScans.map((scan) => (
                  <div
                    key={scan.id}
                    data-testid={`scheduled-scan-${scan.id}`}
                    className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-50'}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${scan.is_active ? 'bg-green-500' : 'bg-zinc-500'}`} />
                      <div>
                        <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          {scan.name}
                        </p>
                        <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                          {scan.frequency.charAt(0).toUpperCase() + scan.frequency.slice(1)} • 
                          {scan.scan_all_domains ? ' All domains' : ` ${scan.domain_ids?.length || 0} domains`} • 
                          {scan.total_runs || 0} runs
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {scan.next_run && (
                        <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
                          Next: {new Date(scan.next_run).toLocaleDateString()}
                        </span>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRunScheduleNow(scan.id)}
                        className={isDarkMode ? 'hover:bg-white/10' : ''}
                        title="Run now"
                      >
                        <Play className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleToggleSchedule(scan)}
                        className={isDarkMode ? 'hover:bg-white/10' : ''}
                        title={scan.is_active ? 'Pause' : 'Activate'}
                      >
                        {scan.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 text-green-500" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteSchedule(scan.id)}
                        className="hover:bg-red-500/10 text-red-500"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className={`text-sm text-center py-4 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
                No scheduled scans configured. Create one to automatically discover new assets.
              </p>
            )}
          </div>

          {/* Discovered External Assets */}
          <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Radar className="w-5 h-5 text-indigo-500" />
                <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Discovered Assets ({externalAssets.length})
                </h3>
                <span className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-100 text-indigo-700'}`}>
                  External Attack Surface
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Select value={exposureFilter} onValueChange={setExposureFilter}>
                  <SelectTrigger className={`w-32 h-8 text-xs ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}>
                    <SelectValue placeholder="Filter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Levels</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={fetchExternalAssets}
                  className={isDarkMode ? 'border-white/10' : ''}
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Bulk Actions Bar */}
            {selectedExternalAssets.size > 0 && (
              <div className={`flex items-center justify-between p-3 rounded-lg mb-4 ${isDarkMode ? 'bg-indigo-500/20 border border-indigo-500/30' : 'bg-indigo-50 border border-indigo-200'}`}>
                <span className={`text-sm font-medium ${isDarkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>
                  <strong>{selectedExternalAssets.size}</strong> asset(s) selected
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleBulkPromoteExternalAssets}
                    className={`${isDarkMode ? 'border-green-500/30 text-green-400 hover:bg-green-500/20' : 'border-green-500 text-green-600 hover:bg-green-50'}`}
                    data-testid="bulk-promote-external-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Promote to Inventory
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleBulkDeleteExternalAssets}
                    className={`${isDarkMode ? 'border-red-500/30 text-red-400 hover:bg-red-500/20' : 'border-red-500 text-red-600 hover:bg-red-50'}`}
                    data-testid="bulk-delete-external-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete Selected
                  </Button>
                </div>
              </div>
            )}
            
            {/* Exposure Stats */}
            {externalAssets.length > 0 && (
              <div className="grid grid-cols-4 gap-2 mb-4">
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-red-500/10 border border-red-500/20' : 'bg-red-50 border border-red-100'}`}>
                  <p className="text-lg font-bold text-red-500">{externalAssets.filter(a => a.exposure_level === 'critical').length}</p>
                  <p className={`text-xs ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>Critical</p>
                </div>
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-orange-500/10 border border-orange-500/20' : 'bg-orange-50 border border-orange-100'}`}>
                  <p className="text-lg font-bold text-orange-500">{externalAssets.filter(a => a.exposure_level === 'high').length}</p>
                  <p className={`text-xs ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>High</p>
                </div>
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-yellow-500/10 border border-yellow-500/20' : 'bg-yellow-50 border border-yellow-100'}`}>
                  <p className="text-lg font-bold text-yellow-500">{externalAssets.filter(a => a.exposure_level === 'medium').length}</p>
                  <p className={`text-xs ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>Medium</p>
                </div>
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-green-500/10 border border-green-500/20' : 'bg-green-50 border border-green-100'}`}>
                  <p className="text-lg font-bold text-green-500">{externalAssets.filter(a => a.exposure_level === 'low' || !a.exposure_level).length}</p>
                  <p className={`text-xs ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>Low</p>
                </div>
              </div>
            )}
            
            {/* Assets Table */}
            {filteredExternalAssets.length > 0 ? (
              <div className={`rounded-lg border overflow-hidden ${isDarkMode ? 'border-white/5' : 'border-slate-200'}`}>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[700px]">
                    <thead className={isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}>
                      <tr>
                        <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                          <input
                            type="checkbox"
                            checked={filteredExternalAssets.length > 0 && filteredExternalAssets.every(a => selectedExternalAssets.has(a.id))}
                            onChange={() => toggleSelectAllExternalAssets(filteredExternalAssets)}
                            className="rounded border-gray-300"
                            data-testid="select-all-external-checkbox"
                          />
                        </th>
                        <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Asset</th>
                        <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Domain</th>
                        <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Exposure</th>
                        <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Ports</th>
                        <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Source</th>
                        <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Discovered</th>
                        <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Actions</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${isDarkMode ? 'divide-white/5' : 'divide-slate-100'}`}>
                      {filteredExternalAssets.slice(0, 20).map((asset) => (
                        <tr 
                          key={asset.id}
                          className={`cursor-pointer transition-colors ${
                            selectedExternalAssets.has(asset.id) 
                              ? (isDarkMode ? 'bg-indigo-500/20' : 'bg-indigo-50') 
                              : (isDarkMode ? 'hover:bg-white/5' : 'hover:bg-slate-50')
                          }`}
                        >
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedExternalAssets.has(asset.id)}
                              onChange={() => toggleExternalAssetSelection(asset.id)}
                              className="rounded border-gray-300"
                              data-testid={`select-external-${asset.id}`}
                            />
                          </td>
                          <td className="px-4 py-3" onClick={() => { setSelectedAsset(asset); setAssetDetailOpen(true); }}>
                            <div className="flex items-center gap-2">
                              <Globe className={`w-4 h-4 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-500'}`} />
                              <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                {asset.name || asset.host}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                              {asset.domain || '-'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-1 rounded ${getExposureBadge(asset.exposure_level)}`}>
                              {asset.exposure_level || 'Unknown'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {asset.open_ports?.slice(0, 3).map((port, idx) => (
                                <span key={idx} className={`text-xs px-1.5 py-0.5 rounded font-mono ${isDarkMode ? 'bg-zinc-700 text-zinc-300' : 'bg-slate-100 text-slate-600'}`}>
                                  {port}
                                </span>
                              ))}
                              {asset.open_ports?.length > 3 && (
                                <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
                                  +{asset.open_ports.length - 3}
                                </span>
                              )}
                              {!asset.open_ports?.length && <span className={isDarkMode ? 'text-zinc-500' : 'text-slate-400'}>-</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-zinc-700 text-zinc-300' : 'bg-slate-100 text-slate-600'}`}>
                              {asset.source || 'discovery'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                              {asset.discovered_at ? new Date(asset.discovered_at).toLocaleDateString() : '-'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  promoteExternalAsset(asset);
                                }}
                                className={`text-green-500 hover:text-green-600 hover:bg-green-500/10`}
                                data-testid={`promote-external-asset-${asset.id}`}
                                title="Promote to Inventory"
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteExternalAsset(asset.id);
                                }}
                                className={`text-red-500 hover:text-red-600 hover:bg-red-500/10`}
                                data-testid={`delete-external-asset-${asset.id}`}
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {filteredExternalAssets.length > 20 && (
                  <div className={`px-4 py-3 text-center border-t ${isDarkMode ? 'bg-zinc-800/30 border-white/5' : 'bg-slate-50 border-slate-100'}`}>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                      Showing 20 of {filteredExternalAssets.length} assets
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className={`text-center py-12 rounded-lg ${isDarkMode ? 'bg-zinc-800/30' : 'bg-slate-50'}`}>
                <Globe className={`w-12 h-12 mx-auto mb-3 ${isDarkMode ? 'text-zinc-600' : 'text-slate-300'}`} />
                <p className={`font-medium ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>No external assets discovered</p>
                <p className={`text-sm mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                  Add a domain above and run EASM discovery to find external assets
                </p>
                {domains.length > 0 && (
                  <Button
                    onClick={() => handleStartDiscovery(domains[0]?.domain)}
                    disabled={discovering}
                    className="mt-4 bg-indigo-600 hover:bg-indigo-500 text-white"
                  >
                    {discovering ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Discovering...
                      </>
                    ) : (
                      <>
                        <Radar className="w-4 h-4 mr-2" />
                        Start Discovery
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Discovery Scanner Tab */}
        <TabsContent value="discovery" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className={isDarkMode ? 'border-white/10 text-white hover:bg-white/10' : ''}>
                    <Settings className="w-4 h-4 mr-2" />
                    Configure Scan
                  </Button>
                </DialogTrigger>
                <DialogContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
                  <DialogHeader>
                    <DialogTitle>Scan Configuration</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Scan Type</Label>
                      <Select value={scanConfig.scan_type} onValueChange={(v) => setScanConfig({...scanConfig, scan_type: v})}>
                        <SelectTrigger className={isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="network">Network Scan</SelectItem>
                          <SelectItem value="port">Port Scan</SelectItem>
                          <SelectItem value="service">Service Detection</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Target Range</Label>
                      <Input
                        value={scanConfig.target_range}
                        onChange={(e) => setScanConfig({...scanConfig, target_range: e.target.value})}
                        placeholder="192.168.1.0/24"
                        className={isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}
                      />
                    </div>
                    <div>
                      <Label>Ports</Label>
                      <Input
                        value={scanConfig.ports}
                        onChange={(e) => setScanConfig({...scanConfig, ports: e.target.value})}
                        placeholder="22,80,443,3306,5432"
                        className={isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}
                      />
                    </div>
                    <div>
                      <Label>Scan Depth</Label>
                      <Select value={scanConfig.scan_depth} onValueChange={(v) => setScanConfig({...scanConfig, scan_depth: v})}>
                        <SelectTrigger className={isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="quick">Quick</SelectItem>
                          <SelectItem value="standard">Standard</SelectItem>
                          <SelectItem value="deep">Deep</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <Button
              onClick={startScan}
              disabled={scanning}
              className="bg-indigo-600 hover:bg-indigo-500 text-white"
            >
              {scanning ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Scanning...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Start Scan
                </>
              )}
            </Button>
          </div>

          {/* Scan Progress */}
          {scanning && (
            <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Scanning: {scanConfig.target_range}
                </span>
                <span className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                  {scanProgress}%
                </span>
              </div>
              <Progress value={scanProgress} className="h-2" />
            </div>
          )}

          {/* Discovered Assets */}
          <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Discovered Assets ({filteredDiscoveredAssets.length})
              </h3>
              {filteredDiscoveredAssets.length > 0 && (
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => toggleSelectAllDiscoveredAssets(filteredDiscoveredAssets)}
                    className={isDarkMode ? 'border-white/10 text-xs' : 'text-xs'}
                    data-testid="select-all-discovered-btn"
                  >
                    {filteredDiscoveredAssets.every(a => selectedDiscoveredAssets.has(a.id)) ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
              )}
            </div>

            {/* Bulk Actions Bar for Discovered Assets */}
            {selectedDiscoveredAssets.size > 0 && (
              <div className={`flex items-center justify-between p-3 rounded-lg mb-4 ${isDarkMode ? 'bg-blue-500/20 border border-blue-500/30' : 'bg-blue-50 border border-blue-200'}`}>
                <span className={`text-sm font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                  <strong>{selectedDiscoveredAssets.size}</strong> asset(s) selected
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleBulkPromoteDiscoveredAssets}
                    className={`${isDarkMode ? 'border-green-500/30 text-green-400 hover:bg-green-500/20' : 'border-green-500 text-green-600 hover:bg-green-50'}`}
                    data-testid="bulk-promote-discovered-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Promote to Inventory
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleBulkDeleteDiscoveredAssets}
                    className={`${isDarkMode ? 'border-red-500/30 text-red-400 hover:bg-red-500/20' : 'border-red-500 text-red-600 hover:bg-red-50'}`}
                    data-testid="bulk-delete-discovered-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete Selected
                  </Button>
                </div>
              </div>
            )}

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {filteredDiscoveredAssets.map((asset, idx) => (
                <div
                  key={idx}
                  data-testid={`discovered-asset-${idx}`}
                  className={`p-4 rounded-lg flex items-center justify-between ${
                    selectedDiscoveredAssets.has(asset.id)
                      ? (isDarkMode ? 'bg-blue-500/20 border border-blue-500/30' : 'bg-blue-50 border border-blue-200')
                      : (isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50')
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selectedDiscoveredAssets.has(asset.id)}
                      onChange={() => toggleDiscoveredAssetSelection(asset.id)}
                      className="rounded border-gray-300"
                      data-testid={`select-discovered-${asset.id}`}
                    />
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-blue-600/20' : 'bg-blue-100'}`}>
                      <Network className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                    </div>
                    <div>
                      <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {asset.hostname || asset.ip_address}
                      </p>
                      <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        {asset.ip_address} • {asset.os || 'Unknown OS'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {asset.services && asset.services.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {asset.services.slice(0, 3).map((svc, sidx) => (
                          <span key={sidx} className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-zinc-700 text-zinc-300' : 'bg-slate-200 text-slate-700'}`}>
                            {svc}
                          </span>
                        ))}
                      </div>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => promoteAsset(asset)}
                      className={isDarkMode ? 'border-white/10 text-white hover:bg-white/10' : ''}
                      data-testid={`promote-discovered-${asset.id}`}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add to Inventory
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteDiscoveredAsset(asset.id)}
                      className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                      data-testid={`delete-discovered-asset-${asset.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
              {filteredDiscoveredAssets.length === 0 && (
                <div className={`text-center py-8 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
                  <Radar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No assets discovered yet</p>
                  <p className="text-sm mt-1">Configure and run a scan to discover assets</p>
                </div>
              )}
            </div>
          </div>

          {/* Recent Scans */}
          {scans.length > 0 && (
            <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Recent Scans
              </h3>
              <div className="space-y-2">
                {scans.slice(0, 5).map((scan, idx) => (
                  <div
                    key={idx}
                    className={`p-3 rounded-lg flex items-center justify-between ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}
                  >
                    <div className="flex items-center gap-3">
                      {scan.status === 'completed' ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-yellow-500" />
                      )}
                      <span className={`text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {scan.target_range || scan.scan_type}
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                        {scan.assets_found || 0} found
                      </span>
                      <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        {scan.created_at ? new Date(scan.created_at).toLocaleDateString() : 'N/A'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Asset Detail Dialog */}
      <Dialog open={assetDetailOpen} onOpenChange={setAssetDetailOpen}>
        <DialogContent className={`max-w-2xl ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200'}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className={`w-5 h-5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
              {selectedAsset?.name || selectedAsset?.host}
            </DialogTitle>
          </DialogHeader>
          {selectedAsset && (
            <div className="space-y-4">
              {/* Asset Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Host</p>
                  <p className={`font-mono text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{selectedAsset.host || selectedAsset.ip_address}</p>
                </div>
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Exposure Level</p>
                  <span className={`text-sm px-2 py-0.5 rounded ${getExposureBadge(selectedAsset.exposure_level)}`}>
                    {selectedAsset.exposure_level || 'Unknown'}
                  </span>
                </div>
              </div>

              {/* Open Ports */}
              {selectedAsset.open_ports && selectedAsset.open_ports.length > 0 && (
                <div>
                  <p className={`text-xs mb-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Open Ports</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedAsset.open_ports.map((port, idx) => (
                      <span key={idx} className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-zinc-800 text-zinc-300' : 'bg-slate-200 text-slate-700'}`}>
                        :{port}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Technologies */}
              {selectedAsset.technologies && selectedAsset.technologies.length > 0 && (
                <div>
                  <p className={`text-xs mb-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Technologies</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedAsset.technologies.map((tech, idx) => (
                      <span key={idx} className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-100 text-indigo-700'}`}>
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Remediation Steps */}
              <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-orange-900/20 border-orange-500/30' : 'bg-orange-50 border-orange-200'}`}>
                <p className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                  <Wrench className="w-4 h-4" />
                  Actionable Remediation Steps
                </p>
                <div className="space-y-3">
                  {getRemediationSteps(selectedAsset).map((step, idx) => (
                    <div key={idx} className={`p-3 rounded ${isDarkMode ? 'bg-zinc-800/50' : 'bg-white'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          step.severity === 'critical' ? 'bg-red-500/20 text-red-500' :
                          step.severity === 'high' ? 'bg-orange-500/20 text-orange-500' :
                          step.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-500' :
                          'bg-blue-500/20 text-blue-500'
                        }`}>
                          {step.severity.toUpperCase()}
                        </span>
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          {step.issue}
                        </span>
                      </div>
                      <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                        {step.action}
                      </p>
                      {step.command && (
                        <code className={`block mt-2 text-xs p-2 rounded ${isDarkMode ? 'bg-zinc-900 text-green-400' : 'bg-slate-100 text-green-700'}`}>
                          {step.command}
                        </code>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Domain Detail Dialog */}
      <Dialog open={domainDetailOpen} onOpenChange={setDomainDetailOpen}>
        <DialogContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className={`w-5 h-5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
              {selectedDomain?.domain}
            </DialogTitle>
          </DialogHeader>
          {selectedDomain && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Status</p>
                  <span className={`text-sm px-2 py-0.5 rounded ${selectedDomain.verified ? 'bg-green-500/20 text-green-500' : 'bg-yellow-500/20 text-yellow-500'}`}>
                    {selectedDomain.verified ? 'Verified' : 'Pending Verification'}
                  </span>
                </div>
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Added</p>
                  <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {selectedDomain.created_at ? new Date(selectedDomain.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
              {selectedDomain.description && (
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Description</p>
                  <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{selectedDomain.description}</p>
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className={`flex-1 ${isDarkMode ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' : 'border-red-300 text-red-600 hover:bg-red-50'}`}
                  onClick={() => { setDomainDetailOpen(false); setDeleteDomainDialogOpen(true); }}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Domain
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Domain Confirmation */}
      <AlertDialog open={deleteDomainDialogOpen} onOpenChange={setDeleteDomainDialogOpen}>
        <AlertDialogContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Domain</AlertDialogTitle>
            <AlertDialogDescription className={isDarkMode ? 'text-zinc-400' : ''}>
              Are you sure you want to delete "{selectedDomain?.domain}"? This will also remove all associated assets and monitoring data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className={isDarkMode ? 'bg-zinc-800 text-white hover:bg-zinc-700' : ''}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDomain}
              className="bg-red-600 hover:bg-red-500 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Promote Dialog with Grouping/Tagging */}
      <Dialog open={promoteDialogOpen} onOpenChange={setPromoteDialogOpen}>
        <DialogContent className={`max-w-md ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}`}>
          <DialogHeader>
            <DialogTitle className={isDarkMode ? 'text-white' : ''}>
              Promote Assets to Inventory
            </DialogTitle>
            <DialogDescription className={isDarkMode ? 'text-zinc-400' : ''}>
              Configure grouping and tagging for {promoteSource === 'external' ? selectedExternalAssets.size : selectedDiscoveredAssets.size} selected asset(s)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className={isDarkMode ? 'text-zinc-300' : ''}>Asset Group (optional)</Label>
              <Input
                placeholder="e.g., Production, Development, DMZ"
                value={promoteConfig.group}
                onChange={(e) => setPromoteConfig(prev => ({ ...prev, group: e.target.value }))}
                className={isDarkMode ? 'bg-zinc-800 border-white/10 text-white' : ''}
                data-testid="promote-group-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className={isDarkMode ? 'text-zinc-300' : ''}>Tags (comma-separated)</Label>
              <Input
                placeholder="e.g., web-server, critical, production"
                value={promoteConfig.tags}
                onChange={(e) => setPromoteConfig(prev => ({ ...prev, tags: e.target.value }))}
                className={isDarkMode ? 'bg-zinc-800 border-white/10 text-white' : ''}
                data-testid="promote-tags-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={isDarkMode ? 'text-zinc-300' : ''}>Category</Label>
                <Select 
                  value={promoteConfig.category} 
                  onValueChange={(v) => setPromoteConfig(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger className={isDarkMode ? 'bg-zinc-800 border-white/10 text-white' : ''}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="external">External</SelectItem>
                    <SelectItem value="internal">Internal</SelectItem>
                    <SelectItem value="cloud">Cloud</SelectItem>
                    <SelectItem value="iot">IoT</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className={isDarkMode ? 'text-zinc-300' : ''}>Asset Type</Label>
                <Select 
                  value={promoteConfig.type} 
                  onValueChange={(v) => setPromoteConfig(prev => ({ ...prev, type: v }))}
                >
                  <SelectTrigger className={isDarkMode ? 'bg-zinc-800 border-white/10 text-white' : ''}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="external">External</SelectItem>
                    <SelectItem value="infrastructure">Infrastructure</SelectItem>
                    <SelectItem value="web">Web Application</SelectItem>
                    <SelectItem value="api">API</SelectItem>
                    <SelectItem value="database">Database</SelectItem>
                    <SelectItem value="mobile">Mobile</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPromoteDialogOpen(false)}
              className={isDarkMode ? 'border-white/10' : ''}
            >
              Cancel
            </Button>
            <Button
              onClick={executeBulkPromote}
              className="bg-green-600 hover:bg-green-500 text-white"
              data-testid="execute-promote-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Promote to Inventory
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
